/**
 * Theme variants for the shared registration auth components.
 *
 * - 'dark': the original literal stone/dgr-gold palette used by the DGR and
 *   DGD monolithic register pages. These strings must stay byte-identical to
 *   the literals they replaced — DGR/DGD pass no theme prop and rely on this
 *   default.
 * - 'semantic': shadcn semantic tokens (bg-card, border-input, text-foreground,
 *   bg-primary, text-destructive, ...). Tenant theming comes from the
 *   [data-tenant] CSS scope in globals.css — e.g. the UWR scope resolves these
 *   to a light neutral palette with UWR-orange primary/ring. Used by the
 *   registration engine (RegistrationFlow).
 */
export type RegistrationTheme = 'dark' | 'semantic';

/**
 * PhoneInput exposes a `'dark' | 'light'` variant rather than the registration
 * `'dark' | 'semantic'` theme. This maps one to the other so the country
 * picker matches the surrounding form (UWR/semantic → light + orange primary;
 * DGR/DGD/dark → the stone literals). Registration always renders h-12 inputs,
 * so call sites pair this with `size="lg"`.
 */
export const REGISTRATION_PHONE_VARIANT = {
  dark: 'dark',
  semantic: 'light',
} as const satisfies Record<RegistrationTheme, 'dark' | 'light'>;

export const REGISTRATION_THEME_CLASSES = {
  dark: {
    heading: 'text-white',
    subtitle: 'text-stone-400',
    highlight: 'text-dgr-gold',
    formDescription: 'text-stone-400 text-sm mb-4',
    label: 'text-stone-300',
    input: 'h-12 bg-stone-900 border-stone-700 text-white placeholder:text-stone-500',
    disabledInput: 'h-12 bg-stone-900 border-stone-700 text-stone-500 cursor-not-allowed',
    yearPicker: 'h-12 bg-stone-900 border-stone-700 text-white',
    pickerContent: 'bg-stone-900 border-stone-700 text-white max-h-60',
    // hover:text-white is load-bearing: the Combobox trigger's own hover:text-current
    // computes to `inherit` (page default = black on the register pages), turning the
    // trigger text and typed search input invisible on the dark field while hovered.
    combobox:
      'h-12 bg-stone-900 border-stone-700 text-white hover:text-white hover:bg-stone-900/80',
    comboboxPopover: 'bg-stone-900 border-stone-700 text-white',
    fieldError: 'text-red-400 text-sm',
    requiredMark: 'text-red-400',
    passwordHint: 'text-stone-500 text-sm',
    dividerLine: 'h-px flex-1 bg-stone-800',
    dividerLabel: 'text-stone-500 text-sm flex items-center gap-2',
    errorBox: 'flex items-start gap-3 p-4 rounded-lg bg-red-500/10 border border-red-500/20 mb-6',
    errorIcon: 'w-5 h-5 text-red-400 flex-shrink-0 mt-0.5',
    errorTitle: 'text-red-200 font-medium',
    errorBody: 'text-red-200/70 text-sm mt-1',
    errorPrimaryButton: 'bg-dgr-gold text-stone-950 hover:bg-dgr-gold-light',
    errorGhostButton: 'px-0 text-red-100 hover:bg-transparent hover:text-white',
    infoBanner:
      'flex items-start gap-3 p-4 rounded-lg bg-blue-500/10 border border-blue-500/20 mb-6',
    infoBannerIcon: 'w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5',
    infoBannerText: 'text-blue-200 text-sm',
    emailBadge: 'flex items-center gap-3 p-3 rounded-lg bg-stone-900 border border-stone-700 mb-6',
    emailBadgeIcon: 'w-5 h-5 text-green-400 flex-shrink-0',
    emailBadgeText: 'text-white text-sm flex-1 truncate',
    emailBadgeChange:
      'text-dgr-gold text-sm hover:underline flex-shrink-0 py-1 px-2 -mr-2 rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-dgr-gold focus-visible:outline-offset-2',
    forgotLink: 'text-dgr-gold text-sm hover:underline',
    noticeBox: 'p-4 rounded-lg bg-stone-900 border border-stone-800 mt-6',
    noticeText: 'text-stone-400 text-sm',
    successBox: 'rounded-lg border border-emerald-700 bg-emerald-950/30 p-4',
    successIcon: 'mt-0.5 h-5 w-5 text-emerald-400',
    successTitle: 'font-medium text-emerald-100',
    successBody: 'mt-1 text-sm text-stone-400',
    // StepFormActions
    actionsDesktopBar: 'hidden items-center justify-between border-t border-stone-800 pt-4 md:flex',
    actionsMobileBar:
      'fixed inset-x-0 bottom-0 z-40 flex flex-col border-t border-stone-800 bg-stone-950/95 px-4 pt-3 backdrop-blur sm:px-6 md:hidden',
    actionsProgressStep: 'text-xs font-medium uppercase tracking-[0.18em] text-stone-500',
    actionsProgressTrack: 'h-1.5 flex-1 overflow-hidden rounded-full bg-stone-800',
    actionsStepLabel: 'truncate text-xs text-stone-400',
    actionsBackGhost: 'text-stone-400 hover:bg-stone-800 hover:text-white',
    actionsBackOutline:
      'border-stone-700 bg-stone-900 text-stone-100 hover:bg-stone-800 hover:text-white',
    actionsPrimaryText: 'text-stone-950',
  },
  semantic: {
    heading: 'text-foreground',
    subtitle: 'text-muted-foreground',
    highlight: 'text-primary',
    formDescription: 'text-muted-foreground text-sm mb-4',
    label: 'text-foreground',
    input: 'h-12 border-input bg-background text-foreground placeholder:text-muted-foreground',
    disabledInput: 'h-12 bg-muted border-input text-muted-foreground cursor-not-allowed',
    yearPicker: 'h-12 border-input bg-background text-foreground',
    pickerContent: 'bg-popover border-border text-popover-foreground max-h-60',
    combobox: 'h-12 border-input bg-background text-foreground hover:bg-accent',
    comboboxPopover: 'bg-popover border-border text-popover-foreground',
    fieldError: 'text-destructive text-sm',
    requiredMark: 'text-destructive',
    passwordHint: 'text-muted-foreground text-sm',
    dividerLine: 'h-px flex-1 bg-border',
    dividerLabel: 'text-muted-foreground text-sm flex items-center gap-2',
    errorBox:
      'flex items-start gap-3 p-4 rounded-lg bg-destructive/10 border border-destructive/20 mb-6',
    errorIcon: 'w-5 h-5 text-destructive flex-shrink-0 mt-0.5',
    errorTitle: 'text-destructive font-medium',
    errorBody: 'text-destructive/80 text-sm mt-1',
    errorPrimaryButton: 'bg-primary text-primary-foreground hover:bg-primary/90',
    errorGhostButton: 'px-0 text-destructive hover:bg-transparent hover:text-destructive/80',
    infoBanner:
      'flex items-start gap-3 p-4 rounded-lg bg-blue-500/10 border border-blue-500/20 mb-6',
    infoBannerIcon: 'w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5',
    infoBannerText: 'text-foreground text-sm',
    emailBadge: 'flex items-center gap-3 p-3 rounded-lg bg-card border border-border mb-6',
    emailBadgeIcon: 'w-5 h-5 text-emerald-600 flex-shrink-0',
    emailBadgeText: 'text-foreground text-sm flex-1 truncate',
    emailBadgeChange:
      'text-primary text-sm hover:underline flex-shrink-0 py-1 px-2 -mr-2 rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-2',
    forgotLink: 'text-primary text-sm hover:underline',
    noticeBox: 'p-4 rounded-lg bg-card border border-border mt-6',
    noticeText: 'text-muted-foreground text-sm',
    successBox: 'rounded-lg border border-emerald-600/30 bg-emerald-600/10 p-4',
    successIcon: 'mt-0.5 h-5 w-5 text-emerald-600',
    successTitle: 'font-medium text-foreground',
    successBody: 'mt-1 text-sm text-muted-foreground',
    // StepFormActions
    actionsDesktopBar: 'hidden items-center justify-between border-t border-border pt-4 md:flex',
    actionsMobileBar:
      'fixed inset-x-0 bottom-0 z-40 flex flex-col border-t border-border bg-background/95 px-4 pt-3 backdrop-blur sm:px-6 md:hidden',
    actionsProgressStep: 'text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground',
    actionsProgressTrack: 'h-1.5 flex-1 overflow-hidden rounded-full bg-muted',
    actionsStepLabel: 'truncate text-xs text-muted-foreground',
    actionsBackGhost: 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
    actionsBackOutline:
      'border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground',
    actionsPrimaryText: 'text-primary-foreground',
  },
} as const satisfies Record<RegistrationTheme, Record<string, string>>;
